import { useCallback, useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  adminGetEcosystems,
  type AdminPackageAvailability,
} from '@frontend/api-clients/ecosystemsClient';
import { fetchAdminProjectAccess } from '@frontend/api-clients/projectAccessClient';
import type { AdminProjectAccessProject, EcosystemAdminEntry } from '@frontend/types';
import CreateEcosystemWizard from './orchestration/CreateEcosystemWizard';
import DownstreamOrphansPanel from './orchestration/DownstreamOrphansPanel';
import MoveProjectPanel from './orchestration/MoveProjectPanel';
import RealmEcosystemDetailsPanel from './RealmEcosystemDetailsPanel';
import RealmEcosystemProjectsTable from './RealmEcosystemProjectsTable';
import RealmEcosystemsList from './RealmEcosystemsList';

export interface EcosystemOrchestrationPanelProps {
  expanded: boolean;
  onToggle: () => void;
}

export default function EcosystemOrchestrationPanel({
  expanded,
  onToggle,
}: EcosystemOrchestrationPanelProps) {
  const [ecosystems, setEcosystems] = useState<EcosystemAdminEntry[]>([]);
  const [packageAvailability, setPackageAvailability] =
    useState<AdminPackageAvailability | null>(null);
  const [loadingEcosystems, setLoadingEcosystems] = useState(false);
  const [ecosystemsError, setEcosystemsError] = useState('');
  const [selectedEcosystemId, setSelectedEcosystemId] = useState<string | null>(null);
  const [projects, setProjects] = useState<AdminProjectAccessProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [selectedProject, setSelectedProject] = useState<AdminProjectAccessProject | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const loadEcosystems = useCallback(async (preferSelectId?: string) => {
    setLoadingEcosystems(true);
    setEcosystemsError('');
    try {
      const token = authClient.getToken();
      if (!token) return;
      const response = await adminGetEcosystems(token);
      const list = response.ecosystems;
      setEcosystems(list);
      setPackageAvailability(response.packageAvailability);
      setSelectedEcosystemId((current) => {
        if (preferSelectId && list.some((e) => e.id === preferSelectId)) return preferSelectId;
        if (current && list.some((e) => e.id === current)) return current;
        return list.find((e) => !e.decommissioned)?.id ?? list[0]?.id ?? null;
      });
    } catch (e) {
      setEcosystemsError(e instanceof Error ? e.message : 'Failed to load ecosystems');
    } finally {
      setLoadingEcosystems(false);
    }
  }, []);

  const loadProjects = useCallback(async (ecosystemId: string) => {
    setLoadingProjects(true);
    setProjectsError('');
    try {
      const token = authClient.getToken();
      if (!token) return;
      const rows = await fetchAdminProjectAccess(ecosystemId, token);
      setProjects(rows);
    } catch (e) {
      setProjects([]);
      setProjectsError(e instanceof Error ? e.message : 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    if (!expanded) return;
    void loadEcosystems();
  }, [expanded, loadEcosystems]);

  useEffect(() => {
    setSelectedProject(null);
    setShowMove(false);
    if (!selectedEcosystemId) {
      setProjects([]);
      return;
    }
    void loadProjects(selectedEcosystemId);
  }, [selectedEcosystemId, loadProjects]);

  const onCreateSuccess = (ecosystemId: string) => {
    setShowCreate(false);
    void loadEcosystems(ecosystemId);
  };

  const onMoveSuccess = () => {
    if (selectedEcosystemId) void loadProjects(selectedEcosystemId);
  };

  return (
    <div className="ecosystem-card admin-widget-card">
      <button type="button" className="admin-widget-header" onClick={onToggle}>
        <div className="admin-widget-header-main">
          <h3 className="ecosystem-title">Ecosystem orchestration</h3>
          <span className="admin-status-badge admin-beta-badge" aria-label="Beta feature">
            Beta
          </span>
        </div>
        <span className="admin-widget-toggle">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded ? (
        <div className="admin-widget-body">
          <p className="ecosystem-description">
            Browse ecosystems and their projects. Add ecosystems here; move only top-level projects
            (sub-projects move with their parent). Top-level project creation lives on the Ecosystem
            Admin page.
          </p>

          <div className="admin-orch-master">
            <div className="admin-orch-section-header">
              <h4 className="admin-orch-subtitle">Ecosystems</h4>
              <button
                type="button"
                className="btn btn-secondary admin-orch-add-badge"
                onClick={() => {
                  setShowMove(false);
                  setShowCreate(true);
                }}
              >
                + Add ecosystem
              </button>
            </div>

            {showCreate ? (
              <div className="admin-orch-create-panel">
                <div className="admin-orch-section-header">
                  <h4 className="admin-orch-subtitle">Add ecosystem</h4>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreate(false)}
                  >
                    Close
                  </button>
                </div>
                <CreateEcosystemWizard
                  packageAvailability={packageAvailability}
                  onSuccess={onCreateSuccess}
                  onCancel={() => setShowCreate(false)}
                />
              </div>
            ) : null}

            {loadingEcosystems ? (
              <p className="ecosystem-description">Loading ecosystems…</p>
            ) : ecosystemsError ? (
              <div className="settings-message settings-error" role="alert">
                {ecosystemsError}
              </div>
            ) : ecosystems.length === 0 ? (
              <p className="ecosystem-description">No ecosystems in this realm yet.</p>
            ) : (
              <div className="admin-orch-master-detail">
                <RealmEcosystemsList
                  ecosystems={ecosystems}
                  selectedEcosystemId={selectedEcosystemId}
                  onSelect={setSelectedEcosystemId}
                />
                <RealmEcosystemDetailsPanel
                  ecosystem={
                    ecosystems.find((e) => e.id === selectedEcosystemId) ?? null
                  }
                  packageAvailability={packageAvailability}
                  onUpdated={(id) => void loadEcosystems(id)}
                />
              </div>
            )}

            {selectedEcosystemId ? (
              <div className="admin-orch-projects-section">
                <div className="admin-orch-section-header">
                  <h4 className="admin-orch-subtitle">Projects in ecosystem</h4>
                  {selectedProject && !showMove ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowCreate(false);
                        setShowMove(true);
                      }}
                    >
                      Move project
                    </button>
                  ) : null}
                </div>
                <p className="ecosystem-description">
                  Select a top-level project to reveal Move. Sub-projects are listed for context
                  only.
                </p>
                <RealmEcosystemProjectsTable
                  projects={projects}
                  selectedProjectId={selectedProject?.id ?? null}
                  onSelectTopLevel={(project) => {
                    setSelectedProject(project);
                    setShowMove(false);
                  }}
                  loading={loadingProjects}
                  error={projectsError}
                />

                {showMove && selectedProject ? (
                  <div className="admin-orch-move-panel">
                    <MoveProjectPanel
                      project={{
                        id: selectedProject.id,
                        name: selectedProject.name || selectedProject.id,
                        ecosystemId: selectedProject.ecosystemId || selectedEcosystemId || '',
                      }}
                      ecosystems={ecosystems}
                      onCancel={() => {
                        setShowMove(false);
                        setSelectedProject(null);
                      }}
                      onSuccess={onMoveSuccess}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <DownstreamOrphansPanel
            ecosystems={ecosystems}
            onImported={() => {
              if (selectedEcosystemId) void loadProjects(selectedEcosystemId);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
